import { useTranslation } from '@mint-lib/i18n';
import { useFlags } from '@mint-lib/routing-context';
import type {} from '@mint-lib/theme/src/extensions/palette.js';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Command,
  CommandGroup,
  CommandList,
  typographyVariants,
} from '@mint-lib/tw-components';

import { useGetFilterList } from '../../../api/queries/useGetFilterList.js';
import { useIntegrationsFilter } from '../../../hooks/useIntegrationsFilter.js';
import SkeletonsBox from '../../_atoms/SkeletonsBox/SkeletonsBox.jsx';
import FilterOption from './FilterOption.jsx';
import { OptionItem } from './Filters.types.js';

const Filters = () => {
  const { t } = useTranslation('@myn/integrations_v2');
  const ff = useFlags('@mint/arm', ['settingsMint2']);

  const isSettingsV2 = ff?.settingsMint2?.enabled;

  const {
    data: {
      offlineChannels = [],
      onlineChannels = [],
      categories = [],
      channels = [],
    },
    isLoading,
  } = useGetFilterList();

  const {
    navigate,
    search,
    sort,
    status,
    categoryUrl: category,
    channelUrl: channel,
    categoryArray: selectedCategories,
    channelArray: selectedChannels,
  } = useIntegrationsFilter();

  const handleReset = () => {
    navigate({
      search: {
        search,
        sort,
        status,
        category: '',
        channel: '',
      },
    });
  };

  const handleOnlineItems = () => {
    const isAllOnlineItemsSelected = onlineChannels.every((channel) =>
      selectedChannels.includes(channel.name),
    );

    navigate({
      search: {
        search,
        sort,
        status,
        category,
        channel: isAllOnlineItemsSelected
          ? ''
          : onlineChannels.map((channel) => channel.name).join(','),
      },
    });
  };

  const handleOfflineItems = () => {
    const isAllOfflineItemsSelected = offlineChannels.every((channel) =>
      selectedChannels.includes(channel.name),
    );

    navigate({
      search: {
        search,
        sort,
        status,
        category,
        channel: isAllOfflineItemsSelected
          ? ''
          : offlineChannels.map((channel) => channel.name).join(','),
      },
    });
  };

  const handleSelectChannel = (itemName: string, isChecked: boolean) => {
    navigate({
      search: {
        search,
        sort,
        status,
        category,
        channel: isChecked
          ? selectedChannels.filter((id) => id !== itemName).join(',')
          : [...selectedChannels, itemName].join(','),
      },
    });
  };

  const handleSelectCategory = (itemName: string, isChecked: boolean) => {
    const newCategoryValue = isChecked
      ? selectedCategories.filter((id) => id !== itemName)
      : [...selectedCategories, itemName];
    navigate({
      search: {
        search,
        sort,
        status,
        category: newCategoryValue.join(','),
        channel,
      },
    });
  };

  const isSomeOfflineChannelSelected = offlineChannels.some((channel) =>
    selectedChannels.includes(channel.name),
  );
  const isSomeOnlineChannelSelected = onlineChannels.some((channel) =>
    selectedChannels.includes(channel.name),
  );

  if (isLoading) {
    return (
      <div>
        <SkeletonsBox
          skeletonCount={2}
          skeletonClassName="rounded w-21 h-4"
          wrapperClassName="flex justify-between items-center w-80 px-1.5 pt-3"
        />
        <SkeletonsBox
          skeletonCount={3}
          skeletonClassName="rounded w-77 h-4"
          wrapperClassName="flex flex-col items-center gap-3 pt-4"
        />
      </div>
    );
  }

  return (
    <div className="flex h-full w-115 flex-col items-start justify-start overflow-hidden p-0">
      <div className="flex w-full items-center justify-between px-3">
        <h6 className={typographyVariants({ variant: 'h6' })}>
          {t('Filter by')}
        </h6>
        <Button
          color="secondary"
          size="medium"
          variant="text"
          onClick={handleReset}
          disabled={!selectedChannels.length && !selectedCategories.length}
          data-dd-action-name="reset filters"
        >
          {t('Reset')}
        </Button>
      </div>
      <div className="relative w-full flex-1">
        <div className="absolute top-0 left-0 m-0 h-full min-h-0 w-full overflow-scroll pb-16">
          {!isSettingsV2 && !!channels?.length && (
            <Accordion collapsible type="single" defaultValue="channel-panel">
              <AccordionItem value="channel-panel" variant="secondary">
                <AccordionTrigger title={t('Channel')} />
                <AccordionContent className="pt-0 pl-0">
                  <Command>
                    <CommandList className="max-h-none">
                      <CommandGroup>
                        <FilterOption
                          key="ONLINE"
                          checked={isSomeOnlineChannelSelected}
                          item={{ name: 'ONLINE', label: t('ONLINE') }}
                          onClick={handleOnlineItems}
                          level={0}
                        />
                        {onlineChannels.map((item: OptionItem) => (
                          <FilterOption
                            key={item.name}
                            checked={selectedChannels.includes(item.name)}
                            item={item}
                            onClick={handleSelectChannel}
                            level={1}
                          />
                        ))}
                      </CommandGroup>
                    </CommandList>

                    <CommandList className="max-h-none">
                      <CommandGroup>
                        <FilterOption
                          key="OFFLINE"
                          checked={isSomeOfflineChannelSelected}
                          item={{ name: 'OFFLINE', label: t('OFFLINE') }}
                          onClick={handleOfflineItems}
                          level={0}
                        />
                        {offlineChannels.map((item: OptionItem) => (
                          <FilterOption
                            key={item.name}
                            checked={selectedChannels.includes(item.name)}
                            item={item}
                            onClick={handleSelectChannel}
                            level={1}
                          />
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}

          <Accordion collapsible type="single" defaultValue="category-panel">
            <AccordionItem value="category-panel" variant="secondary">
              <AccordionTrigger title={t('Category')} />

              <AccordionContent className="pt-0 pl-0">
                <Command>
                  <CommandList>
                    <CommandGroup>
                      {categories.map((item: OptionItem) => (
                        <FilterOption
                          key={item.name}
                          checked={selectedCategories.includes(item.name)}
                          item={item}
                          onClick={handleSelectCategory}
                          level={0}
                        />
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
    </div>
  );
};

export default Filters;
